/**
 * useVehiclesInspectionsDashboardOverview - Indicadores da aba Vistoria.
 *
 * Toda a leitura é consolidada na RPC segura do dashboard; o navegador não
 * consulta tabelas diretamente.
 *
 * @version 1.1.0
 * RELEASE: 2026-10-03 v1.1.0 - Veículos únicos, listas operacionais e feeds
 *          infinitos com cursores compostos, todos pela RPC segura existente.
 * RELEASE: 2026-10-03 v1.0.0 - Criação inicial.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { VehiclesDashboardFilters } from '@/hooks/useVehiclesDashboardOverview';

const logger = createLogger('useVehiclesInspectionsDashboardOverview');

export const VEHICLES_INSPECTIONS_DASHBOARD_QUERY_KEY = 'vehicles-inspections-dashboard-overview';

export interface VehiclesInspectionsDashboardFilters extends VehiclesDashboardFilters {
  checklistTypeId?: string | null;
  templateId?: string | null;
  assigneeMode?: 'user' | 'queue' | 'owner' | 'provider' | null;
}

export interface InspectionDashboardDistribution {
  id: string;
  value: number;
}

export interface InspectionTemplatePerformance {
  id: string;
  label: string;
  color: string;
  total: number;
  approved: number;
  rejected: number;
  corrections: number;
  avg_hours: number;
}

export interface InspectionResponsiblePerformance {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  assigned: number;
  completed: number;
  on_time_rate: number;
  avg_hours: number;
  rejected: number;
  with_fraud: number;
}

export interface VehiclesInspectionsDashboardOverview {
  consolidated: {
    total: number;
    completed: number;
    completion_rate: number;
    unique_vehicles: number;
    by_origin: { manual: number; schedule: number };
  };
  kpis: { open: number; overdue: number; awaiting_review: number; with_fraud: number };
  monthly: Array<{ month: string; created: number; completed: number; approved: number; rejected: number }>;
  by_status: InspectionDashboardDistribution[];
  by_deadline: InspectionDashboardDistribution[];
  by_template: InspectionTemplatePerformance[];
  critical_items: Array<{ id: string; label: string; value: number }>;
  performance: InspectionResponsiblePerformance[];
  oldest_open: InspectionFeedItem[];
}

export interface InspectionFeedItem {
  id: string;
  inspection_id?: string;
  code: string;
  status: string;
  created_at?: string;
  moved_at?: string;
  accepted_at?: string;
  template_name: string;
  vehicle_plate: string | null;
  vehicle_label: string;
  signer_name?: string;
  role?: string;
  purpose?: string;
}

type JsonObject = Record<string, unknown>;
const objectValue = (value: unknown): JsonObject => value && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : {};
const arrayValue = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const n = (value: unknown) => Number(value) || 0;

/** Normaliza uma linha operacional sem confiar no formato livre do JSONB. */
const normalizeFeedItem = (value: unknown): InspectionFeedItem => {
  const row = objectValue(value);
  return {
    id: String(row.id ?? ''),
    inspection_id: typeof row.inspection_id === 'string' ? row.inspection_id : undefined,
    code: String(row.code ?? '—'),
    status: String(row.status ?? ''),
    created_at: typeof row.created_at === 'string' ? row.created_at : undefined,
    moved_at: typeof row.moved_at === 'string' ? row.moved_at : undefined,
    accepted_at: typeof row.accepted_at === 'string' ? row.accepted_at : undefined,
    template_name: String(row.template_name ?? '—'),
    vehicle_plate: typeof row.vehicle_plate === 'string' ? row.vehicle_plate : null,
    vehicle_label: String(row.vehicle_label ?? 'Veículo não identificado'),
    signer_name: typeof row.signer_name === 'string' ? row.signer_name : undefined,
    role: typeof row.role === 'string' ? row.role : undefined,
    purpose: typeof row.purpose === 'string' ? row.purpose : undefined,
  };
};

/** Monta os argumentos comuns para manter filtros idênticos entre resumo e feeds. */
const commonRpcArgs = (filters: VehiclesInspectionsDashboardFilters, tenantId: string) => ({
  p_start_date: filters.startDate.toISOString(), p_end_date: filters.endDate.toISOString(),
  p_vehicle_type: filters.vehicleType, p_category_id: filters.categoryId, p_status_id: filters.statusId,
  p_type_id: filters.checklistTypeId ?? null, p_template_id: filters.templateId ?? null,
  p_assignee_mode: filters.assigneeMode ?? null,
  p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
  p_tenant_hint: tenantId,
});

/** Normaliza a resposta JSONB para impedir que valores ausentes quebrem o painel. */
function normalizeOverview(value: unknown): VehiclesInspectionsDashboardOverview {
  const raw = objectValue(value);
  const consolidated = objectValue(raw.consolidated);
  const origins = objectValue(consolidated.by_origin);
  const kpis = objectValue(raw.kpis);
  const distributions = (key: string) => arrayValue(raw[key]).map((item) => {
    const row = objectValue(item);
    return { id: String(row.id ?? ''), value: n(row.value) };
  });
  return {
    consolidated: {
      total: n(consolidated.total), completed: n(consolidated.completed),
      completion_rate: n(consolidated.completion_rate),
      unique_vehicles: n(consolidated.unique_vehicles),
      by_origin: { manual: n(origins.manual), schedule: n(origins.schedule) },
    },
    kpis: { open: n(kpis.open), overdue: n(kpis.overdue), awaiting_review: n(kpis.awaiting_review), with_fraud: n(kpis.with_fraud) },
    monthly: arrayValue(raw.monthly).map((item) => {
      const row = objectValue(item);
      return { month: String(row.month ?? ''), created: n(row.created), completed: n(row.completed), approved: n(row.approved), rejected: n(row.rejected) };
    }),
    by_status: distributions('by_status'),
    by_deadline: distributions('by_deadline'),
    by_template: arrayValue(raw.by_template).map((item) => {
      const row = objectValue(item);
      return { id: String(row.id ?? ''), label: String(row.label ?? '—'), color: String(row.color ?? 'hsl(var(--primary))'), total: n(row.total), approved: n(row.approved), rejected: n(row.rejected), corrections: n(row.corrections), avg_hours: n(row.avg_hours) };
    }),
    critical_items: arrayValue(raw.critical_items).map((item) => {
      const row = objectValue(item);
      return { id: String(row.id ?? ''), label: String(row.label ?? '—'), value: n(row.value) };
    }),
    performance: arrayValue(raw.performance).map((item) => {
      const row = objectValue(item);
      return { user_id: String(row.user_id ?? ''), full_name: String(row.full_name ?? '—'), avatar_url: typeof row.avatar_url === 'string' ? row.avatar_url : null, assigned: n(row.assigned), completed: n(row.completed), on_time_rate: n(row.on_time_rate), avg_hours: n(row.avg_hours), rejected: n(row.rejected), with_fraud: n(row.with_fraud) };
    }),
    oldest_open: arrayValue(raw.oldest_open).map(normalizeFeedItem),
  };
}

export function useVehiclesInspectionsDashboardOverview(filters?: VehiclesInspectionsDashboardFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [VEHICLES_INSPECTIONS_DASHBOARD_QUERY_KEY, tenantId, filters],
    enabled: Boolean(tenantId && filters),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      if (!tenantId || !filters) throw new Error('Filtros da aba Vistoria indisponíveis');
      // Preserva o contexto interno do cliente Supabase em RPCs fora da tipagem gerada.
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        name: string, args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
      const { data, error } = await rpc('get_vehicles_inspections_dashboard_overview', commonRpcArgs(filters, tenantId));
      if (error) {
        logger.error('Erro ao carregar a aba Vistoria', { error: error.message });
        throw new Error(error.message);
      }
      return normalizeOverview(data);
    },
  });
}

type InspectionFeedMode = 'recently_moved' | 'latest_acceptances';

/** Carrega feeds operacionais em lotes fixos de 10, com cursor por data e ID. */
export function useVehiclesInspectionsDashboardFeed(
  mode: InspectionFeedMode,
  filters?: VehiclesInspectionsDashboardFilters,
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useInfiniteQuery({
    queryKey: [VEHICLES_INSPECTIONS_DASHBOARD_QUERY_KEY, 'feed', mode, tenantId, filters],
    enabled: Boolean(tenantId && filters),
    initialPageParam: null as { at: string; id: string } | null,
    queryFn: async ({ pageParam }) => {
      if (!tenantId || !filters) throw new Error('Filtros da aba Por Vistoria indisponíveis');
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        name: string, args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
      const { data, error } = await rpc('get_vehicles_inspections_dashboard_overview', {
        ...commonRpcArgs(filters, tenantId),
        p_feed_mode: mode,
        p_cursor_at: pageParam?.at ?? null,
        p_cursor_id: pageParam?.id ?? null,
        p_page_size: 10,
      });
      if (error) {
        logger.error('Erro ao carregar feed da aba Por Vistoria', { mode, error: error.message });
        throw new Error(error.message);
      }
      return arrayValue(data).map(normalizeFeedItem);
    },
    getNextPageParam: (page) => {
      if (page.length < 10) return undefined;
      const last = page[page.length - 1];
      const at = mode === 'recently_moved' ? last?.moved_at : last?.accepted_at;
      return last && at ? { at, id: last.id } : undefined;
    },
    staleTime: 60_000,
  });
}